import React, { useState, useEffect, useMemo } from 'react';
import { FleetSensor } from '../types';
import { fetchFleetStatus, fetchOwners, updateSensor, Owner } from '../services/adminService';
import { getProductInfo } from '../services/productCatalog';
import { RefreshCw, LogOut, ShieldCheck, Activity, Pencil, ExternalLink, RadioTower, Radio, HeartPulse, HelpCircle, Users, Plus, Database } from 'lucide-react';
import SensorEditModal from './SensorEditModal';
import CustomersModal from './CustomersModal';
import AddGatewayModal from './AddGatewayModal';
import RawReadingsModal from './RawReadingsModal';

interface AdminDashboardProps {
  onLogout: () => void;
}

const OFFLINE_THRESHOLD_MS = 6 * 60 * 60 * 1000; // 6 hours
const BATTERY_KEYS = ['battery_pct', 'battery_cV', 'battery_v', 'battery'];

// Means "nobody has looked at this since it registered itself" — NOT "has no
// owner". A node inherits the owner of whichever gateway heard it, so it
// normally has one already; what it lacks is a human confirming what it is.
// Calling it "unassigned" made that look like a contradiction in the table.
// Editing the device in the panel is what clears it.
const isUnclaimed = (s: FleetSensor) => !s.claimedAt;

const ROLE_LABELS: Record<string, string> = {
  gateway: 'gateway',
  node: 'node',
  telemetry: 'telemetry',
};

// Two icons per row, answering two different questions: the product icon says
// what it measures, this one says what it is. A gateway and a battery node can
// both be M3TRmon and behave nothing alike.
const ROLE_ICONS: Record<string, { Icon: typeof RadioTower; className: string; title: string }> = {
  gateway:   { Icon: RadioTower, className: 'text-emerald-400', title: 'Gateway — uploads directly and relays for nodes' },
  node:      { Icon: Radio,      className: 'text-sky-400',     title: 'Node — battery, reaches the internet via a gateway' },
  telemetry: { Icon: HeartPulse, className: 'text-violet-400',  title: "Telemetry — a gateway's own health identity" },
};
const STATUS_KEYS = ['status', 'error', 'flag_error'];

const isOnline = (lastSeen: string | null): boolean => {
  if (!lastSeen) return false;
  return Date.now() - new Date(lastSeen).getTime() < OFFLINE_THRESHOLD_MS;
};

const formatLastSeen = (lastSeen: string | null): string => {
  if (!lastSeen) return 'Never';
  return new Date(lastSeen).toLocaleString();
};

const extractField = (reading: Record<string, unknown> | null, keys: string[]): string => {
  if (!reading) return '—';
  for (const key of keys) {
    if (reading[key] !== undefined && reading[key] !== null) {
      return String(reading[key]);
    }
  }
  return '—';
};

// Devices disagree on how they report battery: LoRa nodes send centivolts
// (battery_cV), the WiFi gateways send volts (battery_v). Showing the raw
// number made a node read "512" next to a gateway's "4.97".
const formatBattery = (reading: Record<string, unknown> | null): string => {
  if (!reading) return '—';
  if (reading.battery_cV != null) return `${(Number(reading.battery_cV) / 100).toFixed(2)}V`;
  if (reading.battery_v != null) return `${Number(reading.battery_v).toFixed(2)}V`;
  if (reading.battery_pct != null) return `${reading.battery_pct}%`;
  return '—';
};

const ALL = '__all__';

const AdminDashboard: React.FC<AdminDashboardProps> = ({ onLogout }) => {
  const [sensors, setSensors] = useState<FleetSensor[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState(ALL);
  const [ownerFilter, setOwnerFilter] = useState(ALL);
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [roleFilter, setRoleFilter] = useState(ALL);
  const [owners2, setOwners2] = useState<Owner[]>([]);
  const [editing, setEditing] = useState<FleetSensor | null>(null);
  const [showCustomers, setShowCustomers] = useState(false);
  const [showAddGateway, setShowAddGateway] = useState(false);
  const [viewingRaw, setViewingRaw] = useState<FleetSensor | null>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const result = await fetchFleetStatus();
      fetchOwners().then(setOwners2);
      setSensors(result);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const types = useMemo(() => Array.from(new Set(sensors.map(s => s.type))).sort(), [sensors]);
  const owners = useMemo(
    () => Array.from(new Set(sensors.map(s => s.ownerName).filter((n): n is string => !!n))).sort(),
    [sensors]
  );

  const filtered = useMemo(() => {
    return sensors.filter(s => {
      if (typeFilter !== ALL && s.type !== typeFilter) return false;
      if (roleFilter === 'unclaimed' && !isUnclaimed(s)) return false;
      if (roleFilter !== ALL && roleFilter !== 'unclaimed' && s.role !== roleFilter) return false;
      if (ownerFilter !== ALL && s.ownerName !== ownerFilter) return false;
      if (statusFilter !== ALL) {
        const online = isOnline(s.lastSeen);
        if (statusFilter === 'online' && !online) return false;
        if (statusFilter === 'offline' && online) return false;
      }
      return true;
    });
  }, [sensors, typeFilter, ownerFilter, statusFilter, roleFilter]);

  const unclaimedCount = useMemo(() => sensors.filter(isUnclaimed).length, [sensors]);

  return (
    <div className="min-h-screen bg-slate-900 pb-20">
      <nav className="sticky top-0 z-20 bg-slate-900/90 backdrop-blur-md border-b border-slate-700 px-4 py-4 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <ShieldCheck className="text-cyan-400 w-5 h-5" />
          <span className="font-bold text-lg text-slate-100">SensorLink Admin</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadData}
            className="p-2 bg-slate-800 rounded-full text-slate-400 hover:text-cyan-400 hover:bg-slate-700 transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowAddGateway(true)}
            title="Add gateway"
            className="p-2 bg-slate-800 rounded-full text-slate-400 hover:text-cyan-400 hover:bg-slate-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowCustomers(true)}
            title="Customers"
            className="p-2 bg-slate-800 rounded-full text-slate-400 hover:text-cyan-400 hover:bg-slate-700 transition-colors"
          >
            <Users className="w-5 h-5" />
          </button>
          <button onClick={onLogout} className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-full transition-colors">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </nav>

      <main className="p-4 max-w-[1600px] mx-auto">
        <div className="mb-6 flex items-center gap-2">
          <Activity className="w-5 h-5 text-cyan-400" />
          <h1 className="text-2xl font-bold text-slate-100">Fleet Status</h1>
          <span className="text-sm text-slate-500">({filtered.length} of {sensors.length} sensors)</span>
          {unclaimedCount > 0 && (
            // A node that registered itself is waiting for a human. Surfacing
            // the count here means you notice without going looking for it.
            <button
              onClick={() => setRoleFilter('unclaimed')}
              className="ml-auto px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/15 text-amber-400 border border-amber-500/30 hover:bg-amber-500/25"
            >
              {unclaimedCount} needs review — click to see
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4">
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="bg-slate-800 border border-slate-700 text-slate-300 text-sm rounded-lg px-3 py-2"
          >
            <option value={ALL}>All Roles</option>
            <option value="gateway">Gateways</option>
            <option value="node">Nodes</option>
            <option value="telemetry">Telemetry</option>
            <option value="unclaimed">Needs review</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
          >
            <option value={ALL}>All Types</option>
            {types.map(t => <option key={t} value={t}>{getProductInfo(t)?.name ?? t}</option>)}
          </select>
          <select
            value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value)}
            className="bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
          >
            <option value={ALL}>All Owners</option>
            {owners.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-slate-800 border border-slate-700 text-slate-200 text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
          >
            <option value={ALL}>All Statuses</option>
            <option value="online">Online</option>
            <option value="offline">Offline (&gt;6h)</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700 text-slate-500 text-xs uppercase tracking-wide">
                <th className="text-left px-4 py-3 font-medium">Device</th>
                <th className="text-left px-4 py-3 font-medium">Type</th>
                <th className="text-left px-4 py-3 font-medium">Role</th>
                <th className="text-left px-4 py-3 font-medium">Owner</th>
                <th className="text-left px-4 py-3 font-medium">Location</th>
                <th className="text-left px-4 py-3 font-medium">Lifecycle</th>
                <th className="text-left px-4 py-3 font-medium">Last Seen</th>
                <th className="text-left px-4 py-3 font-medium">Battery</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-right px-4 py-3 font-medium">Msgs 24h</th>
                <th className="text-right px-4 py-3 font-medium">Msgs 7d</th>
                <th className="text-right px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={12} className="text-center py-10 text-slate-500">
                    {loading ? 'Loading…' : 'No sensors match the current filters.'}
                  </td>
                </tr>
              ) : (
                filtered.map(s => {
                  const product = getProductInfo(s.type);
                  const Icon = product?.icon;
                  const online = isOnline(s.lastSeen);
                  return (
                    <tr key={s.sensorId} className="border-b border-slate-700/50 last:border-0 hover:bg-slate-700/20">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-200 flex items-center gap-2">
                          {s.alias || s.sensorName}
                          {s.alias && <span className="text-xs text-slate-500">({s.sensorName})</span>}
                          {isUnclaimed(s) && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-amber-500/15 text-amber-400 border border-amber-500/30">
                              unassigned
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                          {(() => {
                            const r = ROLE_ICONS[s.role ?? ''];
                            const RoleIcon = r?.Icon ?? HelpCircle;
                            return <RoleIcon className={`w-3.5 h-3.5 ${r?.className ?? 'text-slate-600'}`} aria-label={r?.title ?? 'unknown role'} />;
                          })()}
                          <span className="font-mono">{s.deviceId ?? '—'}</span>
                          {/* A LoRa node's firmware version, relayed with each reading */}
                          {typeof s.latestReading?.fw_version === 'number' && (
                            <span className="font-mono">v{s.latestReading.fw_version}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 text-slate-300">
                          {Icon && <Icon className="w-3.5 h-3.5 text-slate-500" />}
                          {product?.name ?? s.type}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-slate-400 bg-slate-700/40 border border-slate-600/50 rounded px-1.5 py-0.5">
                          {ROLE_LABELS[s.role ?? ''] ?? '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        {s.ownerName && s.ownerSecretLinkId ? (
                          // Opens the customer's own dashboard, exactly as they
                          // see it — the fastest way to check what a customer is
                          // actually looking at when they report a problem.
                          <a
                            href={`#/view/${s.ownerSecretLinkId}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-sky-400 hover:text-sky-300 hover:underline"
                            title="Open this customer's dashboard"
                          >
                            {s.ownerName}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          s.ownerName ?? '—'
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-300">{s.location ?? '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-1.5 py-0.5 rounded border ${
                          s.status === 'deployed' ? 'text-slate-400 bg-slate-700/40 border-slate-600/50'
                          : s.status === 'rma' ? 'text-red-400 bg-red-500/10 border-red-500/30'
                          : s.status === 'retired' ? 'text-slate-500 bg-slate-800 border-slate-700'
                          : 'text-amber-400 bg-amber-500/10 border-amber-500/30'
                        }`} title={s.notes ?? undefined}>
                          {s.status ?? '—'}
                        </span>
                      </td>
                      <td className={`px-4 py-3 ${online ? 'text-slate-300' : 'text-red-400 font-medium'}`}>
                        {formatLastSeen(s.lastSeen)}
                      </td>
                      <td className="px-4 py-3 text-slate-300">{formatBattery(s.latestReading)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
                          online ? 'bg-green-500/10 text-green-400 border border-green-500/30' : 'bg-red-500/10 text-red-400 border border-red-500/30'
                        }`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${online ? 'bg-green-400' : 'bg-red-400'}`} />
                          {online ? 'Online' : 'Offline'}
                        </span>
                        {extractField(s.latestReading, STATUS_KEYS) !== '—' && (
                          <div className="text-xs text-slate-500 mt-1">code: {extractField(s.latestReading, STATUS_KEYS)}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-300">{s.messages24h}</td>
                      <td className="px-4 py-3 text-right text-slate-300">{s.messages7d}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setViewingRaw(s)}
                          className="text-slate-400 hover:text-cyan-400 p-1"
                          title="Raw readings"
                        >
                          <Database className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditing(s)}
                          className="text-slate-400 hover:text-sky-400 p-1"
                          title="Assign / edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {viewingRaw && (
          <RawReadingsModal sensor={viewingRaw} onClose={() => setViewingRaw(null)} />
        )}

        {showAddGateway && (
          <AddGatewayModal
            owners={owners2}
            onClose={() => setShowAddGateway(false)}
            onCreated={loadData}
          />
        )}

        {showCustomers && (
          <CustomersModal
            owners={owners2}
            sensors={sensors}
            onClose={() => setShowCustomers(false)}
            onChanged={loadData}
          />
        )}

        {editing && (
          <SensorEditModal
            sensor={editing}
            owners={owners2}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); loadData(); }}
            onSave={updateSensor}
          />
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
